import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Mail, ArrowLeft, Download } from "lucide-react";
import { authMe, authLogout, adminSubmissions, adminDeleteSubmission, adminNewsletter } from "../lib/api";

const KIND_LABEL = { student: "Student", professional: "Interviewee", partner: "Partner" };

const Eyebrow = ({ children }) => (
  <div className="flex items-center gap-3 mb-3">
    <span className="h-px w-8 bg-medblue" />
    <span className="text-[10px] uppercase tracking-[0.28em] text-medblue">{children}</span>
  </div>
);

export default function AdminInbox() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("all"); // all | student | professional | partner | subscribers
  const [submissions, setSubmissions] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    authMe().then(setUser).catch(() => navigate("/admin/login", { replace: true }));
  }, [navigate]);

  const reload = () => {
    setLoading(true);
    Promise.all([
      adminSubmissions(tab === "subscribers" ? "all" : tab).then((d) => setSubmissions(d.submissions || [])),
      adminNewsletter().then((d) => setSubscribers(d.subscribers || [])),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { if (user) reload(); }, [user, tab]);

  const onDelete = async (s) => {
    if (!window.confirm(`Delete submission from ${s.name}?`)) return;
    try { await adminDeleteSubmission(s.id); toast.success("Deleted"); setSelected(null); reload(); }
    catch { toast.error("Delete failed"); }
  };

  const exportCSV = () => {
    const rows = [["email", "signup_date"], ...subscribers.map((s) => [s.email, s.signup_date])];
    const csv = rows.map((r) => r.map((c) => `"${(c || "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `subscribers-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  const logout = async () => { await authLogout(); navigate("/admin/login", { replace: true }); };

  const list = tab === "subscribers" ? subscribers : submissions;

  return (
    <div className="min-h-screen bg-parchment" data-testid="admin-inbox">
      <header className="bg-white border-b border-rule">
        <div className="container-wide h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="font-serif text-xl text-navy">Tomorrow&rsquo;s Doctors</Link>
            <span className="text-[10px] uppercase tracking-[0.24em] text-medblue">Admin · Inbox</span>
          </div>
          <div className="flex items-center gap-4">
            {user && (
              <div className="hidden sm:flex items-center gap-3 text-sm text-navy/70">
                {user.picture && <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />}
                <span>{user.email}</span>
              </div>
            )}
            <button onClick={logout} className="text-xs uppercase tracking-[0.2em] text-navy/70 hover:text-navy inline-flex items-center gap-1">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="container-wide py-12">
        <div className="flex items-center justify-between mb-6">
          <Link to="/admin" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-navy/60 hover:text-navy">
            <ArrowLeft size={13} /> Back to interviews
          </Link>
          {tab === "subscribers" && subscribers.length > 0 && (
            <button onClick={exportCSV} className="btn-secondary inline-flex" data-testid="export-csv">
              <Download size={14} className="mr-2" /> Export CSV
            </button>
          )}
        </div>

        <Eyebrow>Inbox</Eyebrow>
        <h1 className="font-serif text-4xl text-navy mb-8">Submissions &amp; subscribers.</h1>

        <div className="flex flex-wrap gap-2 border-b border-rule pb-4 mb-8" data-testid="inbox-tabs">
          {[
            { k: "all", label: `All (${submissions.length})` },
            { k: "student", label: "Students" },
            { k: "professional", label: "Interviewees" },
            { k: "partner", label: "Partners" },
            { k: "subscribers", label: `Newsletter (${subscribers.length})` },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => { setTab(t.k); setSelected(null); }}
              className={`text-[11px] uppercase tracking-[0.2em] px-4 py-2 border transition-colors ${
                tab === t.k ? "bg-navy text-white border-navy" : "bg-white text-navy/70 border-rule hover:border-navy hover:text-navy"
              }`}
              data-testid={`tab-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 bg-white border border-rule">
            {loading ? (
              <div className="p-10 text-center text-navy/50 text-sm">Loading…</div>
            ) : list.length === 0 ? (
              <div className="p-14 text-center">
                <div className="font-serif text-2xl text-navy/35 italic">Nothing here yet.</div>
                <p className="mt-3 text-sm text-navy/50">New submissions will appear here in real time.</p>
              </div>
            ) : tab === "subscribers" ? (
              <ul>
                {subscribers.map((s) => (
                  <li key={s.id} className="p-5 border-b border-rule last:border-0 flex items-center gap-3" data-testid={`subscriber-${s.id}`}>
                    <Mail size={14} className="text-medblue" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-navy truncate">{s.email}</div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-navy/40 mt-1">
                        {new Date(s.signup_date).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <ul>
                {submissions.map((s) => (
                  <li key={s.id}>
                    <button
                      onClick={() => setSelected(s)}
                      className={`w-full text-left p-5 border-b border-rule last:border-0 hover:bg-parchment/60 transition-colors ${selected?.id === s.id ? "bg-parchment" : ""}`}
                      data-testid={`submission-${s.id}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-medblue">{KIND_LABEL[s.kind] || s.kind}</div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-navy/40">
                          {new Date(s.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                        </div>
                      </div>
                      <div className="mt-2 font-serif text-lg text-navy leading-tight">{s.name}</div>
                      <div className="mt-1 text-xs text-navy/60 truncate">{s.email}</div>
                      {s.message && <div className="mt-2 text-sm text-navy/60 line-clamp-2">{s.message}</div>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="lg:col-span-7">
            {selected ? (
              <div className="bg-white border border-rule p-8" data-testid="submission-detail">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-medblue">{KIND_LABEL[selected.kind]}</div>
                    <h2 className="font-serif text-3xl text-navy mt-2">{selected.name}</h2>
                    <a href={`mailto:${selected.email}`} className="text-sm text-medblue hover:text-navy inline-flex items-center gap-1 mt-2">
                      <Mail size={13} /> {selected.email}
                    </a>
                  </div>
                  <button onClick={() => onDelete(selected)} className="p-2 text-navy/60 hover:text-destructive" title="Delete" data-testid="delete-submission">
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="mt-8 divide-y divide-rule border-t border-rule">
                  {[
                    ["School", selected.school],
                    ["Grade", selected.grade],
                    ["Interests", selected.interests],
                    ["Profession", selected.profession],
                    ["Specialty", selected.specialty],
                    ["Organization", selected.organization],
                    ["Availability", selected.availability],
                    ["Message", selected.message],
                    ["Received", new Date(selected.created_at).toLocaleString()],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="py-4 grid grid-cols-3 gap-4">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-navy/50">{k}</div>
                      <div className="col-span-2 text-sm text-navy whitespace-pre-line">{v}</div>
                    </div>
                  ))}
                </div>
                <a href={`mailto:${selected.email}?subject=Re: your Tomorrow's Doctors inquiry`} className="btn-primary mt-8 inline-flex">
                  Reply via email
                </a>
              </div>
            ) : (
              <div className="bg-white border border-dashed border-rule p-14 text-center">
                <div className="font-serif text-2xl text-navy/35 italic">
                  {tab === "subscribers" ? "Newsletter subscribers appear on the left." : "Select a submission to view details."}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
